import React, { useState, useCallback, useRef } from "react";
import { useSessionGuard } from "@ayla/auth";
import { SessionWarningModal } from "@ayla/ui";
import { apiClient } from "@/lib/apiClient";
import { useAuthStore } from "../state/authStore";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";

interface LogoutResponse {
  status: string;
  /** Returned by backend for full IdP logout; ignored for app-only logout. */
  sso_logout_url?: string;
}

export function SessionGuardWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const logout = useAuthStore(s => s.logout);

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningModalSeedMs, setWarningModalSeedMs] = useState(0);

  // Ref bridge so the onWarning closure can read getTimeRemaining without a
  // stale-closure problem (getTimeRemaining is returned by useSessionGuard
  // which is called after onWarning is defined).
  const getTimeRemainingRef = useRef<() => number>(() => 0);

  const completeLogout = useCallback(
    async ({ expired }: { expired: boolean }) => {
      try {
        await apiClient.post<LogoutResponse>("/auth/logout");
      } catch {
        // Server call failed (e.g. network offline). Continue with client-side
        // cleanup — the backend JWT blacklist will expire the token on its own.
      }

      localStorage.clear();
      logout(); // clears user-email cookie + resets Zustand auth state

      if (expired) {
        // sonner deduplicates by id — only one toast per session expiry event,
        // even when multiple tabs fire simultaneously.
        toast("Your session has expired for security", {
          id: "session-expired",
        });
      }

      navigate(expired ? "/login?reason=session-expired" : "/login");
    },
    [logout, navigate]
  );

  const handleSecurityLogout = useCallback(async () => {
    await completeLogout({ expired: true });
  }, [completeLogout]);

  const handleManualLogout = useCallback(async () => {
    await completeLogout({ expired: false });
  }, [completeLogout]);

  const { extendSession, getTimeRemaining } = useSessionGuard({
    product: "ENGAGE",
    onWarning: () => {
      // Capture the exact time remaining at the moment the warning fires so
      // the modal countdown is seeded correctly.
      setWarningModalSeedMs(getTimeRemainingRef.current());
      setShowWarningModal(true);
    },
    onActivity: () => setShowWarningModal(false),
    onLogout: handleSecurityLogout,
  });

  // Keep the ref current after every render so onWarning always reads fresh.
  getTimeRemainingRef.current = getTimeRemaining;

  const handleStayLoggedIn = useCallback(() => {
    extendSession();
    setShowWarningModal(false);
  }, [extendSession]);

  return (
    <>
      {children}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="fixed right-4 top-4 z-50 gap-2 bg-background/90 shadow-sm backdrop-blur"
        onClick={handleManualLogout}
      >
        <LogOut className="h-4 w-4" />
        Log Out
      </Button>
      <SessionWarningModal
        isOpen={showWarningModal}
        timeRemainingMs={warningModalSeedMs}
        onStayLoggedIn={handleStayLoggedIn}
        onLogoutNow={handleSecurityLogout}
      />
    </>
  );
}
